import { Component, OnInit, ViewChild, inject, Input } from '@angular/core';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  GsEventSelectorComponent,
  SelectionMeta,
} from 'src/app/shared/features/event-selector/gs-event-selector.component';
import { UtilsService } from 'src/app/private/services/utils.service';
import { DialogCloseReason } from '../../../layout/dialog/app-dialog.service';
import { Discount } from '../../models/discount.model';
import { DiscountsService } from '../../services/discounts.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-discount-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.css'],
  imports: [
    BoDialogComponent,
    ButtonComponent,
    FormsModule,
    GsEventSelectorComponent,
    NgIcon,
  ],
  viewProviders: [provideIcons({ lucideX })],
})
export class DiscountEditComponent implements OnInit {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private discountsService = inject(DiscountsService);
  utils = inject(UtilsService);
  private toastr = inject(ToastrService);

  @ViewChild(GsEventSelectorComponent)
  eventSelector: GsEventSelectorComponent;

  @Input({ required: true }) item: Discount;
  public selectedEventIds: number[] = [];
  public isAllEventsSelected = false;
  public hashValidator = {
    code: false,
    description: false,
    discountPercentage: false,
  };

  ngOnInit(): void {
    if (this.item.validFrom) {
      this.item.validFrom = this.toLocalDatetime(this.item.validFrom);
    }
    if (this.item.validUntil) {
      this.item.validUntil = this.toLocalDatetime(this.item.validUntil);
    }
    this.selectedEventIds = this.item.events?.map(e => e.id) || [];
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    if (this.checkInputs(this.item)) return;
    const itemToSave = {
      ...this.item,
      validFrom: this.item.validFrom
        ? this.toUtcDatetime(this.item.validFrom)
        : null,
      validUntil: this.item.validUntil
        ? this.toUtcDatetime(this.item.validUntil)
        : null,
    };
    const update$ = this.discountsService
      .update$(itemToSave, this.selectedEventIds ?? [])
      .subscribe({
        next: () => {
          update$.unsubscribe();
          this.onDismiss('success');
        },
        error: err => {
          this.toastr.error(this.getApiErrorMessage(err));
        },
      });
  }

  public onEventSelectionChange(ids: number[]) {
    this.selectedEventIds = ids;
  }

  public onEventSelectionMeta(meta: SelectionMeta) {
    this.isAllEventsSelected = meta.isAllSelected;
  }

  public onSelectAllEventsChange(event: any) {
    this.eventSelector.selectAll(event.target.checked);
  }

  private checkInputs(instance: Discount): boolean {
    let hasErrors = false;
    Object.keys(this.hashValidator).forEach(value => {
      (this.hashValidator as any)[value] = false;
    });
    if (this.utils.isEmpty(instance.code)) {
      this.hashValidator.code = true;
      hasErrors = true;
    }
    if (this.utils.isEmpty(instance.description)) {
      this.hashValidator.description = true;
      hasErrors = true;
    }
    if (
      instance.discountPercentage <= 0 ||
      instance.discountPercentage >= 100
    ) {
      this.hashValidator.discountPercentage = true;
      hasErrors = true;
    }
    return hasErrors;
  }

  private getApiErrorMessage(err: unknown): string {
    const errorBody = (err as { error?: Record<string, unknown> })?.error;
    const error = errorBody?.['error'] as
      | {
          message?: string;
          data?: Array<{ message?: string; path?: string[] }>;
        }
      | undefined;

    if (error?.data && Array.isArray(error.data) && error.data.length > 0) {
      const firstError = error.data[0];
      const path = firstError.path?.join('.') || 'field';
      return `${path}: ${firstError.message || 'Invalid value'}`;
    }

    if (
      Array.isArray(errorBody?.['errors']) &&
      errorBody['errors'].length > 0
    ) {
      return String(
        (errorBody['errors'][0] as { message?: string }).message ??
          'An error occurred'
      );
    }

    if (error?.message) {
      return error.message;
    }

    if (typeof errorBody?.['message'] === 'string') {
      return errorBody['message'];
    }

    return 'An error occurred while saving the discount.';
  }

  private toLocalDatetime(utcString: string): string {
    const date = new Date(utcString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  private toUtcDatetime(localString: string): string {
    const date = new Date(localString);
    return date.toISOString();
  }
}
